import axios from "axios";
import { refreshAccessToken } from "../services/authService";

// Flag to prevent multiple simultaneous refresh requests
let isRefreshing = false;
let failedQueue = [];

/**
 * Check if request should skip Bearer token authentication
 * Services can opt-out by setting config.skipAuth = true or config.public = true
 * @param {Object} config - Axios request config
 * @returns {boolean} - True if should skip Bearer token
 */
const shouldSkipAuth = (config) => {
  // Check if service explicitly set skipAuth or public flag
  if (config?.skipAuth === true || config?.public === true) {
    return true;
  }

  // Special case: refresh endpoint never needs Bearer token (uses cookie)
  const url = config?.url || "";
  if (url.includes("/auth/refresh")) {
    return true;
  }

  return false;
};

/**
 * Check if the request URL is an auth endpoint that should NEVER trigger token refresh.
 * These endpoints must be excluded FIRST to prevent infinite refresh loops.
 *
 * Why exclude these?
 * - /auth/login, /auth/google-login: Public endpoints, 401 means wrong credentials (not expired token)
 * - /auth/refresh: The refresh endpoint itself - if it returns 401, we're already logged out
 * - /auth/logout: Logout endpoint - 401 here means we're already logged out
 *
 * @param {string} url - Request URL
 * @returns {boolean} - True if this is an excluded auth endpoint
 */
const isExcludedAuthEndpoint = (url) => {
  if (!url) return false;

  const excludedEndpoints = [
    "/auth/login",
    "/auth/google-login",
    "/auth/refresh",
    "/auth/logout",
  ];

  return excludedEndpoints.some((endpoint) => url.includes(endpoint));
};

/**
 * Process queued requests after token refresh completes
 * @param {Error|null} error - Error if refresh failed, null if succeeded
 * @param {string|null} token - New token if refresh succeeded, null if failed
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });

  failedQueue = [];
};

/**
 * Setup axios interceptors for automatic token management
 * @param {Function} getToken - Function to get current access token
 * @param {Function} setToken - Function to update access token
 * @param {Function} logout - Function to logout user (should clear auth state)
 */
export const setupAxiosInterceptors = (getToken, setToken, logout) => {
  // Request Interceptor: Add access token to all requests
  axios.interceptors.request.use(
    (config) => {
      const skipAuth = shouldSkipAuth(config);

      // Only add Bearer token if service didn't opt out
      if (!skipAuth) {
        const token = getToken?.();

        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      // Ensure withCredentials is set for cookie-based refresh tokens
      // (needed for refresh token cookie and some public endpoints like login)
      config.withCredentials = true;

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response Interceptor: Handle token refresh on 401 errors
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config;

      // Guard 1: Safety check - reject if no request config exists
      if (!originalRequest) {
        return Promise.reject(error);
      }

      const requestUrl = originalRequest.url || "";
      const status = error?.response?.status;

      // Guard 2: EXCLUDED AUTH ENDPOINTS - MUST BE FIRST AND GLOBAL
      // These endpoints must NEVER trigger refresh logic to prevent infinite loops.
      if (isExcludedAuthEndpoint(requestUrl)) {
        return Promise.reject(error);
      }

      // Guard 2.5: If request explicitly opted out of auth, never refresh
      // (Example: public endpoints that do not require a token)
      if (shouldSkipAuth(originalRequest)) {
        return Promise.reject(error);
      }

      // Guard 3: Only attempt refresh on 401 Unauthorized status
      if (status !== 401) {
        return Promise.reject(error);
      }

      // Guard 4: Prevent infinite retry loops
      if (originalRequest._retry) {
        return Promise.reject(error);
      }

      // Guard 5: Handle concurrent requests during refresh
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      // All guards passed - proceed with token refresh
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await refreshAccessToken();

        if (
          response &&
          response.success !== false &&
          (response.accessToken || response.token)
        ) {
          const newToken = response.accessToken || response.token;

          // Update token in storage/state (your AuthContext should handle localStorage if needed)
          setToken?.(newToken);

          // Retry the original request with new token
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // Release queued requests
          processQueue(null, newToken);

          return axios(originalRequest);
        }

        // Refresh failed - user must log in again
        const refreshError = new Error(
          "Token refresh failed - no valid refresh token"
        );
        processQueue(refreshError, null);

        // Clear auth state
        logout?.();

        // Optional hard redirect (keep if you want)
        if (typeof window !== "undefined") {
          window.location.href = "/login";
        }

        return Promise.reject(refreshError);
      } catch (refreshError) {
        // Refresh failed with exception (network error, etc.)
        processQueue(refreshError, null);

        // Clear auth state
        logout?.();

        // Optional hard redirect (keep if you want)
        if (typeof window !== "undefined") {
          window.location.href = "/login";
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
};


